import { useEffect, useState } from "react";
import { ManufacturingGateBanner } from "@/components/manufacturing/ManufacturingGateBanner";
import { useManufacturingEntitlementGate } from "@/components/manufacturing/useManufacturingEntitlementGate";
import { useAuth } from "@/contexts/AuthContext";
import { MFG_FINAL_QC_ROLE } from "@/lib/roles/turboManufacturingRoles";
import { QcDashboardAwaiting } from "./QcDashboardAwaiting";
import { QcDashboardFailures } from "./QcDashboardFailures";
import { QcDashboardFinalQc } from "./QcDashboardFinalQc";
import { QcDashboardOpenReports } from "./QcDashboardOpenReports";
import { readQcDashboard, type QcDashboard } from "@/lib/manufacturing/qcDashboardClient";

export function QcDashboardPanel() {
  const gate = useManufacturingEntitlementGate();
  const auth = useAuth();
  const canFinal = auth.hasRole(MFG_FINAL_QC_ROLE);
  const [page, setPage] = useState(0);
  const [reload, setReload] = useState(0);
  const [dashboard, setDashboard] = useState<QcDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gate.enabled) return;
    let cancelled = false;
    void readQcDashboard(page).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setError(null);
      setDashboard(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [gate.enabled, page, reload]);

  return (
    <div className="w-full space-y-6 text-left">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">QC Dashboard</h2>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          What is waiting, what failed, and which submitted reports still need a correction.
        </p>
      </div>
      <ManufacturingGateBanner gate={gate} />
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {dashboard ? (
        <>
          {canFinal ? <QcDashboardFinalQc reloadKey={reload} /> : null}
          <QcDashboardAwaiting rows={dashboard.awaiting} onDeleted={() => setReload((current) => current + 1)} />
          <QcDashboardFailures
            rows={dashboard.failures}
            dashboard={dashboard}
            onPage={setPage}
            onDeleted={() => setReload((current) => current + 1)}
          />
          <QcDashboardOpenReports
            rows={dashboard.open_reports}
            reportCount={dashboard.open_report_count}
            defectCount={dashboard.open_defect_count}
            onDeleted={() => setReload((current) => current + 1)}
          />
        </>
      ) : !error ? (
        <p className="text-sm text-gray-600 dark:text-zinc-400">Loading QC…</p>
      ) : null}
    </div>
  );
}
